import { isInsuranceItemActive, type PolicyItem } from '../../model/policy-item.ts'

import type {
  NlRcphActivityConfiguration,
  NlRcphActivityId,
  NlRcphSegment,
  NlRcphSubSegment,
} from './nl-rcph-activity-configuration.ts'
import type { NlRcphResolvedActivity } from './nl-rcph-archi-activities.ts'
import { hasNlRcphExecutorMandatoryDoubleLimit } from './nl-rcph-executor-activities.ts'

const SEGMENTS_WITHOUT_RETROACTIVE_COVERAGE_OPTION: readonly NlRcphSegment[] = ['ARCHI', 'MISC_ACCOUNTANTS']
const SEGMENTS_WITHOUT_DOUBLE_LIMIT_OPTION: readonly NlRcphSegment[] = ['ARCHI', 'MISC_ACCOUNTANTS', 'SECU']

const SEGMENTS_WITH_FIXED_DOUBLED_ANNUAL_LIMIT: readonly NlRcphSegment[] = ['ARCHI', 'MISC_ACCOUNTANTS', 'SECU']

export const NL_RCPH_CYBER_MAX_TURNOVER = 2_500_000

export function getNlRcphSegmentFromActivitiesConfigurations(
  activitiesConfigurations: readonly NlRcphActivityConfiguration[] | null | undefined,
): NlRcphSegment | undefined {
  return activitiesConfigurations?.[0]?.segment
}

export function getNlRcphMainActivitySubSegmentFromActivitiesConfigurations(
  activitiesConfigurations: readonly NlRcphActivityConfiguration[] | null | undefined,
): NlRcphSubSegment | undefined {
  return activitiesConfigurations?.[0]?.subSegment
}

export function getNlRcphMainActivityIdFromActivitiesConfigurations(
  activitiesConfigurations:
    | readonly (NlRcphActivityConfiguration & { activityId: NlRcphActivityId })[]
    | null
    | undefined,
): NlRcphActivityId | undefined {
  return activitiesConfigurations?.[0]?.activityId
}

export function isNlRcphGlIncludedForSegment(segment: NlRcphSegment | undefined): boolean {
  return segment === 'SECU'
}

export function isNlRcphGlOptionalAvailable(segment: NlRcphSegment | undefined): boolean {
  return segment != undefined && !isNlRcphGlIncludedForSegment(segment)
}

export function isNlRcphGlActive(segment: NlRcphSegment, glPolicyItem: PolicyItem | null | undefined): boolean {
  if (isNlRcphGlIncludedForSegment(segment)) {
    return true
  }

  return glPolicyItem ? isInsuranceItemActive(glPolicyItem.optionality) : false
}

export function isNlRcphRetroactiveCoverageAvailable(
  segment: NlRcphSegment | undefined,
  companyExistingPolicy: boolean | undefined,
): boolean {
  if (segment == undefined || companyExistingPolicy == undefined) {
    return false
  }

  if (companyExistingPolicy) {
    return false
  }

  return !SEGMENTS_WITHOUT_RETROACTIVE_COVERAGE_OPTION.includes(segment)
}

export function shouldNlRcphRetroactiveCoverageBeExcludedFromQuote(
  segment: NlRcphSegment | undefined,
  companyExistingPolicy: boolean | undefined,
): boolean {
  if (segment == undefined) {
    return false
  }

  if (companyExistingPolicy === true) {
    return true
  }

  return SEGMENTS_WITHOUT_RETROACTIVE_COVERAGE_OPTION.includes(segment)
}

export function shouldNlRcphDoubleLimitBeExcludedFromQuote(segment: NlRcphSegment | undefined): boolean {
  if (segment == undefined) {
    return false
  }

  return !isNlRcphDoubleLimitOptionAvailable(segment)
}

export function shouldNlRcphUsCanadaBeExcludedFromQuote(segment: NlRcphSegment | undefined): boolean {
  if (segment == undefined) {
    return false
  }

  return !isNlRcphUsCanadaAvailable(segment)
}

export function isNlRcphDoubleLimitOptionAvailable(segment: NlRcphSegment | undefined): boolean {
  if (segment == undefined) {
    return false
  }

  return !SEGMENTS_WITHOUT_DOUBLE_LIMIT_OPTION.includes(segment)
}

export function hasNlRcphFixedDoubledAnnualLimit(
  segment: NlRcphSegment | undefined,
  activities: readonly Pick<NlRcphResolvedActivity, 'activityId'>[] = [],
  perClaimLimit?: number,
): boolean {
  if (segment != undefined && SEGMENTS_WITH_FIXED_DOUBLED_ANNUAL_LIMIT.includes(segment)) {
    return true
  }

  if (perClaimLimit != undefined && hasNlRcphExecutorMandatoryDoubleLimit(perClaimLimit, activities)) {
    return true
  }

  return false
}

export function isNlRcphDoubleLimitSelectable(
  segment: NlRcphSegment | undefined,
  activities: readonly Pick<NlRcphResolvedActivity, 'activityId'>[],
  perClaimLimit: number | undefined,
): boolean {
  if (!isNlRcphDoubleLimitOptionAvailable(segment)) {
    return false
  }

  if (perClaimLimit == undefined) {
    return true
  }

  return hasNlRcphExecutorMandatoryDoubleLimit(perClaimLimit, activities) === false
}

export function isNlRcphUsCanadaAvailable(segment: NlRcphSegment | undefined): boolean {
  return segment === 'TECH'
}

export function shouldNlRcphCyberBeExcludedFromQuote(estimatedRevenue: number | undefined): boolean {
  return estimatedRevenue != undefined && estimatedRevenue > NL_RCPH_CYBER_MAX_TURNOVER
}

export const NL_RCPH_REDUCED_PI_LIMITS_MAX_TURNOVER = 2_500_000

// The entry PI limits (100k / 250k) are only offered up to NL_RCPH_REDUCED_PI_LIMITS_MAX_TURNOVER; above it only 500k+.
const NL_RCPH_PI_LIMITS_UNAVAILABLE_ABOVE_MAX_TURNOVER: readonly number[] = [100_000, 250_000]

export function isNlRcphPiLimitAvailableForTurnover(limit: number, estimatedRevenue: number | undefined): boolean {
  if (estimatedRevenue == undefined || estimatedRevenue <= NL_RCPH_REDUCED_PI_LIMITS_MAX_TURNOVER) {
    return true
  }
  return !NL_RCPH_PI_LIMITS_UNAVAILABLE_ABOVE_MAX_TURNOVER.includes(limit)
}
